import { Navigate, Outlet, useLocation } from 'react-router-dom';

import { useUserContext } from '@common/contexts/UserContext';
import usePushNotification from '@common/hooks/usePushNotification';

import { JaiminhoClient } from '@libs/jaiminho';

import { useEffect, useMemo, useRef } from 'react';
import { InstalledApp, useWebBridgeContext } from '@libs/app-bridge';
import { pageNames, RouteMap } from '@common/constants/RouteMap';
import constantsUnlockFeatureTag from '@common/constants/unlockFeatureTag';

import { useAuthentication } from '@libs/acp/contexts/Authentication';
import useAuthenticatedUser from '@libs/acp/hooks/useAuthenticatedUser';

import { getPathNamePermissions } from '@libs/acp/utils/getPathNameAccessPermissions';
import { getFromStorage, setOnStorage } from '@libs/utils/helpers/handleStorage';
import { safeLocalStorage } from '@consumidor-positivo/ts-utils';
import { handleErrorLog } from '@common/utils/handleErrorLogs';
import { useRedline } from '@libs/redline';

const PrivateRoute = () => {
  const { isAuthenticated, tokens } = useAuthentication();
  const { hasPermission } = useAuthenticatedUser();
  const jaiminhoToken = tokens?.accessToken || '';

  const { user } = useUserContext();
  const { isRunningInNativeApp } = useWebBridgeContext();
  const { track: trackPushNotification } = usePushNotification();
  const { track } = useRedline();

  const location = useLocation();
  const { pathname, search } = location;

  const hasTriggeredValidateCredentialsRedirect = useRef(false);

  // TODO: Migrar pra ACP
  useEffect(() => {
    trackPushNotification(user);
  }, [user]);

  // Capra
  const capraStorageKey = 'capra';
  useEffect(() => {
    if (isRunningInNativeApp && isAuthenticated && user?.document) {
      const capraStorage = getFromStorage<{ apps: InstalledApp[]; eventSent: boolean }>(capraStorageKey);

      if (capraStorage && capraStorage?.apps?.length > 0 && !capraStorage?.eventSent) {
        try {
          track.capra.appTracked({ document: user.document, apps: capraStorage.apps });
          setOnStorage('session', capraStorageKey, { ...capraStorage, eventSent: true });
        } catch (err) {
          handleErrorLog(err);
        }
      }
    }
  }, [isRunningInNativeApp, isAuthenticated, user?.document, track]);

  const canAccess = useMemo(() => {
    if (hasPermission('all')) return true;

    const currentPathPermissions = getPathNamePermissions(pageNames, pathname);

    const userPermissions = [];
    currentPathPermissions?.forEach((permission) => {
      if (hasPermission(permission)) userPermissions.push(permission);
    });

    return userPermissions.length > 0;
  }, [pathname]);

  if (!isAuthenticated) {
    const currentPath = pathname + search;

    safeLocalStorage.setItem('redirectAfterLogin', currentPath);

    return <Navigate to={{ pathname: RouteMap.SignIn, search: `${search}` }} replace />;
  }

  const mustValidateCredentials =
    !!isAuthenticated && !pathname.includes(pageNames.myDebts.path) && !canAccess && !hasTriggeredValidateCredentialsRedirect.current;

  if (mustValidateCredentials) {
    hasTriggeredValidateCredentialsRedirect.current = true;
    return (
      <Navigate
        to={{ pathname: pageNames.unlockProtectedFeature.path }}
        replace
        state={{
          featureName: constantsUnlockFeatureTag[pathname?.split('?')[0] as keyof typeof constantsUnlockFeatureTag] || 'DESBLOQUEIE A FUNCIONALIDADE',
          featureSlug: pathname,
        }}
      />
    );
  } else {
    hasTriggeredValidateCredentialsRedirect.current = false;
  }

  return (
    <>
      <Outlet />

      {!isRunningInNativeApp && (
        <JaiminhoClient
          apiKey={import.meta.env.VITE_PUSH_WEB_API_KEY}
          marketplaceApi={import.meta.env.VITE_PUSH_API_CP}
          applicationServerKey={import.meta.env.VITE_PUSH_WEB_SERVER_KEY}
          token={jaiminhoToken}
          options={{
            pushOnTimeout: 15000,
            promptTitle: '',
            promptMessage: 'Olá, você gostaria de ser avisado sobre novas ofertas?',
            promptButton: 'Sim',
            promptDismiss: 'Não',
            bell: false,
            button: false,
            prompt: true,
          }}
        />
      )}
    </>
  );
};

export default PrivateRoute;
